import {
  useEffect,
  useState
} from "react";
import {
  useQuery
} from "@tanstack/react-query";
import {
  ArrowRight,
  Play,
  X
} from "lucide-react";
import {
  Link
} from "react-router";

import {
  getMakingRini,
  getMakingRiniMediaUrl
} from "../../services/catalog";

import {
  findHomeSection,
  useHomeSections
} from "./HomeSectionCms";

function MakingRiniSkeleton() {
  return (
    <section className="rini-section bg-[#eef2e9]">
      <div className="rini-container">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div className="aspect-video animate-pulse rounded-[2rem] bg-black/10" />

          <div>
            <div className="h-3 w-32 animate-pulse rounded bg-black/10" />
            <div className="mt-5 h-12 w-3/4 animate-pulse rounded bg-black/10" />
            <div className="mt-4 h-20 animate-pulse rounded bg-black/10" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function CmsMakingRiniSection() {
  const sectionsQuery =
    useHomeSections();

  const makingQuery =
    useQuery({
      queryKey: [
        "homepage",
        "making-rini"
      ],

      queryFn:
        getMakingRini,

      staleTime: 0,

      refetchOnMount:
        "always",

      refetchOnWindowFocus:
        true
    });

  const [
    videoOpen,
    setVideoOpen
  ] = useState(false);

  const section =
    findHomeSection(
      sectionsQuery.data,
      "MAKING_RINI"
    );

  const content =
    makingQuery.data;

  useEffect(() => {
    if (!videoOpen) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function handleKeyDown(
      event: KeyboardEvent
    ) {
      if (
        event.key === "Escape"
      ) {
        setVideoOpen(
          false
        );
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [videoOpen]);

  if (
    section &&
    !section.isVisible
  ) {
    return null;
  }

  if (
    makingQuery.isLoading ||
    sectionsQuery.isLoading
  ) {
    return (
      <MakingRiniSkeleton />
    );
  }

  const eyebrow =
    section?.eyebrow ??
    "Behind the craft";

  const title =
    section?.title ??
    "Making RiNi";

  const subtitle =
    section?.subtitle ??
    "See how RiNi products are thoughtfully prepared in small batches.";

  const posterUrl =
    content?.posterKey
      ? getMakingRiniMediaUrl(
          content.id,
          "poster",
          content.posterKey
        )
      : null;

  const videoUrl =
    content?.videoKey
      ? getMakingRiniMediaUrl(
          content.id,
          "video",
          content.videoKey
        )
      : null;

  const steps =
    content?.steps
      ?.slice()
      .sort(
        (left, right) =>
          left.displayOrder -
          right.displayOrder
      ) ?? [];

  function openVideo() {
    if (!videoUrl) {
      return;
    }

    setVideoOpen(
      true
    );
  }

  return (
    <>
      <section className="rini-section bg-[#eef2e9]">
        <div className="rini-container">
          <div className="grid gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
            <div>
              <button
                type="button"
                onClick={openVideo}
                disabled={!videoUrl}
                className={[
                  "group relative block aspect-[4/3] w-full overflow-hidden rounded-[2rem] bg-[#d8dfd2]",
                  videoUrl
                    ? "cursor-pointer"
                    : "cursor-default"
                ].join(" ")}
                aria-label={
                  videoUrl
                    ? "Play Making RiNi video"
                    : "Making RiNi"
                }
              >
                {posterUrl ? (
                  <img
                    src={posterUrl}
                    alt="Making RiNi"
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.02]"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-[#80916d] to-[#506141]">
                    <span className="font-serif text-5xl text-white">
                      RiNi
                    </span>
                  </div>
                )}

                <div className="absolute inset-0 bg-black/10" />

                {videoUrl && (
                  <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#5e3b2c] shadow-xl transition duration-300 group-hover:scale-110">
                    <Play
                      size={27}
                      fill="currentColor"
                    />
                  </span>
                )}

                <div className="absolute bottom-5 left-5 rounded-full bg-white/90 px-4 py-2 text-sm font-semibold text-[#5e3b2c] backdrop-blur">
                  {videoUrl
                    ? "Watch the process"
                    : "Made in small batches"}
                </div>
              </button>
            </div>

            <div>
              {eyebrow && (
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                  {eyebrow}
                </p>
              )}

              <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
                {title}
              </h2>

              {subtitle && (
                <p className="mt-5 max-w-xl text-lg leading-8 text-[#746d65]">
                  {subtitle}
                </p>
              )}

              {steps.length > 0 && (
                <div className="mt-8 space-y-4">
                  {steps.map(
                    (step, index) => (
                      <div
                        key={step.id}
                        className="flex items-start gap-4 rounded-2xl border border-[#5e3b2c]/10 bg-white/60 px-5 py-4"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#5e3b2c] text-sm font-semibold text-white">
                          {String(
                            index + 1
                          ).padStart(
                            2,
                            "0"
                          )}
                        </span>

                        <p className="pt-1.5 font-medium leading-6 text-[#49362e]">
                          {step.title}
                        </p>
                      </div>
                    )
                  )}
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                {videoUrl &&
                  content?.ctaText && (
                    <button
                      type="button"
                      onClick={
                        openVideo
                      }
                      className="inline-flex items-center gap-2 rounded-full bg-[#5e3b2c] px-6 py-3.5 font-semibold text-white transition hover:bg-[#38251e]"
                    >
                      <Play
                        size={17}
                      />

                      {content.ctaText}
                    </button>
                  )}

                {content?.ctaUrl &&
                  content.ctaUrl !==
                    "/making-rini" && (
                    <Link
                      to={
                        content.ctaUrl
                      }
                      className="inline-flex items-center gap-2 rounded-full border border-[#5e3b2c]/20 px-6 py-3.5 font-semibold text-[#5e3b2c]"
                    >
                      Explore more

                      <ArrowRight
                        size={17}
                      />
                    </Link>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {videoOpen &&
        videoUrl && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Making RiNi video"
            onMouseDown={(
              event
            ) => {
              if (
                event.target ===
                event.currentTarget
              ) {
                setVideoOpen(
                  false
                );
              }
            }}
          >
            <div className="relative w-full max-w-5xl">
              <button
                type="button"
                onClick={() =>
                  setVideoOpen(
                    false
                  )
                }
                className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#38251e]"
                aria-label="Close video"
              >
                <X size={21} />
              </button>

              <video
                controls
                autoPlay
                preload="metadata"
                playsInline
                poster={
                  posterUrl ??
                  undefined
                }
                className="max-h-[82vh] w-full rounded-2xl bg-black shadow-2xl"
              >
                <source
                  src={videoUrl}
                />

                Your browser does not
                support HTML5 video.
              </video>
            </div>
          </div>
        )}
    </>
  );
}
